import { forwardRef, useLayoutEffect, useRef, useState } from 'react';
import { ListeningOverlay } from './ListeningOverlay';
import {
  PromptInput,
  PromptInputSubmit,
  PromptInputTextarea,
} from '@/components/ai-elements/prompt-input';
import { InputGroupAddon } from '@/components/ui/input-group';
import { VoiceInputButton } from './VoiceInputButton';
import { useIsMobile } from '@/hooks/use-mobile';
import { Plus, X, FileSpreadsheet, FileText, Loader2, Upload } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import DriveSpreadsheetPicker from '@/components/bulk-import/DriveSpreadsheetPicker';
import OneDrivePicker from '@/components/bulk-import/OneDrivePicker';
import { OneDriveLogo } from '@/components/icons/BrandLogos';
import GoogleDriveLogo from '@/components/icons/GoogleDriveLogo';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  ACCEPTED_ATTACHMENT_TYPES,
  MAX_ATTACHMENTS,
  formatBytes,
  readAttachment,
  type ChatAttachment,
} from '@/lib/chatAttachments';

interface Props {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (attachments: ChatAttachment[]) => void;
  onStop: () => void;
  isStreaming: boolean;
  placeholder?: string;
  /** Shorter copy for narrow phone widths, where the full hint wraps and gets clipped. */
  placeholderMobile?: string;
  variant?: 'panel' | 'desk';
}

// Growth ceiling for the composer. Desktop has room for a long draft; on phones
// the on-screen keyboard already halves the viewport, so the cap stays tight
// and the chat history above keeps most of the screen.
const MAX_HEIGHT_DESKTOP = 160;
const MAX_HEIGHT_MOBILE = 96;

export const AskCannafaxComposer = forwardRef<HTMLTextAreaElement, Props>(
  ({ value, onChange, onSubmit, onStop, isStreaming, placeholder, placeholderMobile, variant = 'panel' }, ref) => {
    // Wrap in an object: React would call a bare function passed to setState as an updater.
    const [mic, setMic] = useState<{ meter: () => number } | null>(null);
    const [transcribing, setTranscribing] = useState(false);
    const isMobile = useIsMobile();
    const innerRef = useRef<HTMLTextAreaElement | null>(null);
    const fileRef = useRef<HTMLInputElement | null>(null);
    const [attachments, setAttachments] = useState<ChatAttachment[]>([]);
    const [reading, setReading] = useState(0);
    const [dragging, setDragging] = useState(false);
    const [driveOpen, setDriveOpen] = useState(false);
    const [oneDriveOpen, setOneDriveOpen] = useState(false);

    const addFiles = async (files: File[]) => {
      const room = MAX_ATTACHMENTS - attachments.length - reading;
      if (files.length > room) {
        toast.info(`You can attach up to ${MAX_ATTACHMENTS} files per question.`);
      }
      const batch = files.slice(0, Math.max(0, room));
      setReading((n) => n + batch.length);
      for (const f of batch) {
        try {
          const att = await readAttachment(f);
          setAttachments((prev) => [...prev, att]);
          if (att.truncated) toast.info(`${att.name} is long — only the first part will be read.`);
        } catch (err) {
          toast.error("Couldn't attach that file", { description: err instanceof Error ? err.message : undefined });
        } finally {
          setReading((n) => n - 1);
        }
      }
    };

    // Explicit autosizer: `field-sizing: content` (used by the underlying
    // primitive) is Chromium-only, so Safari/Firefox would stay stuck at one
    // line. Measure scrollHeight instead — start at one line, grow per line,
    // cap, then scroll. Clearing or sending collapses back to one line.
    useLayoutEffect(() => {
      const el = innerRef.current;
      if (!el) return;
      const cap = isMobile ? MAX_HEIGHT_MOBILE : MAX_HEIGHT_DESKTOP;
      el.style.height = 'auto';
      const next = Math.min(el.scrollHeight, cap);
      el.style.height = `${next}px`;
      el.style.overflowY = el.scrollHeight > cap ? 'auto' : 'hidden';
    }, [value, isMobile]);

    return (
    <div
      ref={(element) => {
        const textarea = element?.querySelector('textarea') ?? null;
        innerRef.current = textarea;
        if (typeof ref === 'function') ref(textarea);
        else if (ref) ref.current = textarea;
      }}
      // Both variants float the card on open space (no docked border strip) with room
      // below/around it so the .ai-surface lift is never clipped by the sheet's edge.
      className={variant === 'desk' ? 'bg-background pt-2' : 'bg-background px-4 pb-5 pt-3'}
    >
      <div
        className="relative"
        onDragOver={(e) => { if (e.dataTransfer.types.includes('Files')) { e.preventDefault(); setDragging(true); } }}
        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragging(false); }}
        onDrop={(e) => {
          if (!e.dataTransfer.files.length) return;
          e.preventDefault();
          setDragging(false);
          if (!isStreaming) void addFiles(Array.from(e.dataTransfer.files));
        }}
      >
      <input
        ref={fileRef}
        type="file"
        multiple
        accept={ACCEPTED_ATTACHMENT_TYPES}
        className="hidden"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          e.target.value = '';
          if (files.length) void addFiles(files);
        }}
      />
      <DriveSpreadsheetPicker kind="document" open={driveOpen} onOpenChange={setDriveOpen} onPicked={(file) => void addFiles([file])} />
      <OneDrivePicker kind="document" open={oneDriveOpen} onOpenChange={setOneDriveOpen} onPicked={(file) => void addFiles([file])} />
      <PromptInput
        globalDrop={false}
        onSubmit={(_msg, e) => {
          e.preventDefault();
          if (isStreaming || reading > 0 || !value.trim()) return;
          onSubmit(attachments);
          setAttachments([]);
        }}
      >
        {/* Attached files sit inside the card, above the typing row. */}
      {(attachments.length > 0 || reading > 0) && (
        <ul data-slot="attachment-row" className="order-first flex basis-full flex-wrap gap-1.5 px-1 pt-2" aria-label="Attached files">
          {attachments.map((a) => {
            const Icon = a.kind === 'spreadsheet' ? FileSpreadsheet : FileText;
            return (
              <li key={a.id} className="flex max-w-[16rem] items-center gap-1.5 rounded-lg border bg-muted/50 py-1 pl-2 pr-1 text-xs">
                <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                <span className="truncate font-medium">{a.name}</span>
                <span className="shrink-0 text-muted-foreground">{formatBytes(a.size)}</span>
                <Button variant="quiet" size="icon-sm"
                  type="button"
                  onClick={() => setAttachments((prev) => prev.filter((x) => x.id !== a.id))}
                  className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-background hover:text-foreground"
                  aria-label={`Remove ${a.name}`}
                >
                  <X className="h-3 w-3" />
                </Button>
              </li>
            );
          })}
          {reading > 0 && (
            <li className="flex items-center gap-1.5 rounded-lg border bg-muted/50 px-2 py-1 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> Reading file…
            </li>
          )}
        </ul>
      )}
        {/* Attach lives on the left (source in, actions out) like Claude/ChatGPT;
            voice + send stay on the right. Both addons sit bottom-aligned with the field. */}
        <InputGroupAddon align="inline-start" className="self-end gap-1 pb-1.5">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className="rounded-full"
                disabled={isStreaming || attachments.length + reading >= MAX_ATTACHMENTS}
                aria-label="Attach a file"
                title="Attach a spreadsheet, PDF or text file"
              >
                <Plus className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" side="top">
              <DropdownMenuItem onSelect={() => fileRef.current?.click()}>
                <Upload className="mr-2 h-4 w-4" /> Upload from computer
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setDriveOpen(true)}>
                <GoogleDriveLogo className="mr-2 h-4 w-4" /> Choose from Google Drive
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setOneDriveOpen(true)}>
                <OneDriveLogo className="mr-2 h-4 w-4" /> Choose from OneDrive
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </InputGroupAddon>
        <PromptInputTextarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            // Hide the example text while the mic is active — transcription is about to land here.
            placeholder={
              isStreaming
                ? 'Answering… you can ask your next question in a moment'
                : mic || transcribing
                  ? ''
                  : (isMobile && placeholderMobile ? placeholderMobile : placeholder ?? "Ask about anything in Cannafax…")
            }
            maxLength={2000}
            disabled={isStreaming}
            rows={1}
            data-voice-provisional={transcribing || undefined}
            className="voice-settle min-h-10 w-full overflow-hidden py-2.5 text-base md:text-sm placeholder:truncate disabled:cursor-not-allowed disabled:opacity-60"
          />
        <InputGroupAddon align="inline-end" className="self-end gap-1 pb-1.5">
          <VoiceInputButton
            baseText={value}
            onText={onChange}
            disabled={isStreaming}
            onRecordingChange={(recording, meter) => setMic(recording && meter ? { meter } : null)}
            onTranscribingChange={setTranscribing}
          />

          <PromptInputSubmit
            size="icon-sm"
            status={isStreaming ? 'streaming' : undefined}
            disabled={!isStreaming && (!value.trim() || reading > 0)}
            onStop={onStop}
          />
        </InputGroupAddon>
      </PromptInput>
      {dragging && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-2xl border-2 border-dashed border-primary bg-background/90 text-sm font-medium">
          Drop a spreadsheet, PDF or text file
        </div>
      )}
      {/* Pinned to the typing row (bottom) so it never covers attached files; left-14 clears +, right-24 clears mic + send. */}
      {mic ? <ListeningOverlay meter={mic.meter} className="left-14 right-24 top-auto bottom-[3px] h-[44px]" /> : null}
      {/* Only over an empty field — never on top of a draft the user already has. */}
      {transcribing && !value && (
        <span className="pointer-events-none absolute left-14 bottom-[21px] translate-y-1/2 text-sm italic text-muted-foreground">
          Transcribing…
        </span>
      )}
      </div>
      {variant !== 'desk' && <p className="mt-2 px-1 text-xs text-muted-foreground">
        Answers use your account context and the page you're on.
      </p>}
    </div>
    );
  },
);

AskCannafaxComposer.displayName = 'AskCannafaxComposer';
